export type ActiveGraphEntityCardinality = "single" | "pair" | "multi";
export type ActiveGraphCategory =
  | "price_volume_fx"
  | "valuation"
  | "size"
  | "estimates"
  | "financials"
  | "fixed_income";

export interface ActiveGraphCapability {
  id: string;
  displayName: string;
  category: ActiveGraphCategory;
  description: string;
  aliases: readonly string[];
  cardinality: ActiveGraphEntityCardinality;
}

export const FACTSET_ACTIVE_GRAPH_CAPABILITIES: readonly ActiveGraphCapability[] = [
  {
    id: "P_PRICE",
    displayName: "Closing Price",
    category: "price_volume_fx",
    description: "Closing price line chart for one or more tickers.",
    aliases: ["price", "stock price", "closing price", "share price"],
    cardinality: "multi",
  },
  {
    id: "P_PRICEDUAL",
    displayName: "Closing Price (dual-axis)",
    category: "price_volume_fx",
    description: "Two companies side-by-side on independent y-axes.",
    aliases: ["dual axis price", "two axis price", "dual performance", "compare price"],
    cardinality: "pair",
  },
  {
    id: "P_INDEX",
    displayName: "Indexed Price",
    category: "price_volume_fx",
    description: "Base-100 indexed price for relative price-performance comparison.",
    aliases: ["indexed", "price performance", "relative performance", "rebased", "base 100"],
    cardinality: "multi",
  },
  {
    id: "P_PVOL",
    displayName: "Price + Volume",
    category: "price_volume_fx",
    description: "Security price line with daily trading-volume bars below.",
    aliases: ["price and volume", "stock volume", "volume", "trading volume"],
    cardinality: "single",
  },
  {
    id: "P_RELATIVE",
    displayName: "Relative Strength",
    category: "price_volume_fx",
    description: "Relative-strength comparison between two tickers.",
    aliases: ["relative strength", "rs", "rs performance"],
    cardinality: "pair",
  },
  {
    id: "P_FX_RATE",
    displayName: "FX Rate",
    category: "price_volume_fx",
    description: "Currency exchange rate over time.",
    aliases: ["fx", "fx rate", "currency", "currency pair", "exchange rate", "forex"],
    cardinality: "single",
  },
  {
    id: "P_EX_RATIO",
    displayName: "Price Exchange Ratio",
    category: "price_volume_fx",
    description: "Price exchange ratio between two securities over time.",
    aliases: ["price exchange ratio", "price ratio", "exchange ratio"],
    cardinality: "pair",
  },
  {
    id: "VAL_PE2",
    displayName: "Trailing P/E Ratio (LTM)",
    category: "valuation",
    description:
      "Trailing/LTM Price / Earnings ratio. It does not represent forward/NTM P/E.",
    aliases: [
      "pe",
      "p/e",
      "p e",
      "price to earnings",
      "pe ratio",
      "earnings ratio",
      "trailing pe",
      "trailing p/e",
      "ltm pe",
      "ltm p/e",
      "ttm pe",
      "ttm p/e",
    ],
    cardinality: "multi",
  },
  {
    id: "VAL_EV_EBITD2",
    displayName: "EV / EBITDA",
    category: "valuation",
    description: "Enterprise Value / EBITDA multiple over time.",
    aliases: ["ev/ebitda", "ev ebitda", "enterprise value to ebitda"],
    cardinality: "multi",
  },
  {
    id: "VAL_EV_SALES2",
    displayName: "EV / Sales",
    category: "valuation",
    description: "Enterprise Value / Sales (Revenue) multiple over time.",
    aliases: ["ev/sales", "ev/revenue", "ev sales", "ev revenue"],
    cardinality: "multi",
  },
  {
    id: "VAL_EVAL_EBIT",
    displayName: "EV / EBIT",
    category: "valuation",
    description: "Enterprise Value / EBIT multiple over time.",
    aliases: ["ev/ebit", "ev ebit"],
    cardinality: "multi",
  },
  {
    id: "VAL_PBK2",
    displayName: "P/BV",
    category: "valuation",
    description: "Price / Book Value ratio over time.",
    aliases: ["pb", "p/b", "p bv", "p/bv", "pb ratio", "price to book"],
    cardinality: "multi",
  },
  {
    id: "VAL_PBK_TANG",
    displayName: "P/TBV",
    category: "valuation",
    description: "Price / Tangible Book Value ratio over time.",
    aliases: ["p/tbv", "ptbv", "price to tangible book"],
    cardinality: "multi",
  },
  {
    id: "VAL_PSALES2",
    displayName: "P/S",
    category: "valuation",
    description: "Price / Sales multiple over time.",
    aliases: ["p/s", "ps", "price to sales", "sales multiple"],
    cardinality: "multi",
  },
  {
    id: "VAL_PFCFE",
    displayName: "P/FCF",
    category: "valuation",
    description: "Price / Free Cash Flow to Equity multiple over time.",
    aliases: ["p/fcf", "pfcf", "price to free cash flow"],
    cardinality: "multi",
  },
  {
    id: "VAL_DIV_YLD2",
    displayName: "Dividend Yield",
    category: "valuation",
    description: "Equity dividend-yield percentage over time.",
    aliases: ["dividend yield", "div yield", "stock dividend yield", "yield"],
    cardinality: "multi",
  },
  {
    id: "SIZE_MKT_VAL",
    displayName: "Market Cap",
    category: "size",
    description: "Market value / market capitalisation over time.",
    aliases: [
      "market cap",
      "mkt cap",
      "market capitalization",
      "market capitalisation",
      "market value",
    ],
    cardinality: "multi",
  },
  {
    id: "EST_EPS",
    displayName: "EPS Estimates & History",
    category: "estimates",
    description: "Earnings-per-share history with analyst estimates overlay.",
    aliases: ["eps", "earnings per share", "eps estimates", "eps consensus"],
    cardinality: "single",
  },
  {
    id: "EST_SURPRISE_HISTORY",
    displayName: "EPS Surprise History",
    category: "estimates",
    description: "Historical EPS surprises (reported versus estimate).",
    aliases: ["earnings surprise", "eps surprise", "beat miss"],
    cardinality: "single",
  },
  {
    id: "EST_EBITDA",
    displayName: "EBITDA Estimates",
    category: "estimates",
    description: "Annual mean EBITDA estimates from analysts.",
    aliases: ["ebitda estimate", "ebitda forecast", "ebitda consensus"],
    cardinality: "single",
  },
  {
    id: "EST_NET_INC",
    displayName: "Net Income Estimates",
    category: "estimates",
    description: "Annual mean Net Income estimates from analysts.",
    aliases: ["net income estimate", "net income forecast", "earnings estimate"],
    cardinality: "single",
  },
  {
    id: "EST_SALES",
    displayName: "Sales Estimates",
    category: "estimates",
    description: "Annual mean Sales / Revenue estimates from analysts.",
    aliases: ["sales estimate", "revenue estimate", "sales forecast", "revenue forecast"],
    cardinality: "single",
  },
  {
    id: "EST_TGT_RTNG",
    displayName: "Analyst Targets & Ratings",
    category: "estimates",
    description: "Analyst target prices and ratings over time.",
    aliases: ["analyst rating", "price target", "target price", "buy sell hold"],
    cardinality: "single",
  },
  {
    id: "FIN_SALES",
    displayName: "Revenue History",
    category: "financials",
    description: "Historical revenue / sales as a column chart.",
    aliases: [
      "revenue",
      "sales",
      "revenue history",
      "sales history",
      "historical revenue",
      "top line",
    ],
    cardinality: "single",
  },
  {
    id: "FIN_REV_BUS_SEG",
    displayName: "Revenue by Business Segment",
    category: "financials",
    description: "Revenue breakdown by business segment.",
    aliases: [
      "revenue by business segment",
      "revenue by segment",
      "business segment",
      "segment revenue",
      "segment breakdown",
      "revenue split",
    ],
    cardinality: "single",
  },
  {
    id: "FIN_REV_GEO_SEG",
    displayName: "Revenue by Geography",
    category: "financials",
    description: "Revenue breakdown by geographic segment.",
    aliases: [
      "revenue by geography",
      "geographic segment",
      "revenue by region",
      "geo breakdown",
    ],
    cardinality: "single",
  },
  {
    id: "FIX_YIELD_CURVE",
    displayName: "US Treasury Yield Curve",
    category: "fixed_income",
    description: "Current US Treasury yield curve by maturity.",
    aliases: [
      "yield curve",
      "us treasury yield curve",
      "treasury curve",
      "us treasury yields",
      "rates curve",
    ],
    cardinality: "single",
  },
] as const;

export const FACTSET_ACTIVE_GRAPH_TYPE_IDS = FACTSET_ACTIVE_GRAPH_CAPABILITIES.map(
  ({ id }) => id,
);

const UNSUPPORTED_CHART_PATTERNS = [
  "sankey",
  "treemap",
  "waterfall",
  "funnel",
  "candlestick",
  "ohlc",
  "boxplot",
  "box plot",
  "violin",
  "map chart",
  "world map",
  "geographic map",
  "choropleth",
  "heatmap",
  "gantt",
  "sunburst",
  "histogram",
] as const;

export function isKnownUnsupportedActiveGraphType(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return UNSUPPORTED_CHART_PATTERNS.some((pattern) => normalized.includes(pattern));
}

export interface ActiveGraphEntityConstraint {
  cardinality: ActiveGraphEntityCardinality;
  minTickers: number;
  maxTickers?: number;
  label: string;
}

export function findActiveGraphCapability(
  chartType: string | undefined,
): ActiveGraphCapability | null {
  const normalized = typeof chartType === "string" ? chartType.trim().toUpperCase() : "";
  return (
    FACTSET_ACTIVE_GRAPH_CAPABILITIES.find(({ id }) => id === normalized) ?? null
  );
}

export function getActiveGraphEntityConstraint(
  chartType: string,
): ActiveGraphEntityConstraint | null {
  const capability = findActiveGraphCapability(chartType);
  if (!capability) return null;
  if (capability.cardinality === "single") {
    return { cardinality: "single", minTickers: 1, maxTickers: 1, label: "exactly one ticker" };
  }
  if (capability.cardinality === "pair") {
    return { cardinality: "pair", minTickers: 2, maxTickers: 2, label: "exactly two tickers" };
  }
  return { cardinality: "multi", minTickers: 1, label: "one or more tickers" };
}

function cardinalityPromptLabel(cardinality: ActiveGraphEntityCardinality): string {
  if (cardinality === "single") return "entities: exactly one";
  if (cardinality === "pair") return "entities: exactly two";
  return "entities: one or more";
}

export function buildFactSetCatalogPromptBlock(
  allowedTypeIds?: readonly string[],
): string {
  const allow = allowedTypeIds
    ? new Set(allowedTypeIds.map((id) => id.trim().toUpperCase()))
    : null;
  const groups: Array<{ heading: string; category: ActiveGraphCategory }> = [
    { heading: "Price / volume / FX", category: "price_volume_fx" },
    {
      heading:
        "Valuation multiples (VAL_PE2 is trailing/LTM P/E, not forward/NTM P/E)",
      category: "valuation",
    },
    { heading: "Market size", category: "size" },
    {
      heading: "Estimates / earnings (EPS is EST_EPS, not P_EPS)",
      category: "estimates",
    },
    { heading: "Financials / segments", category: "financials" },
    { heading: "Fixed income", category: "fixed_income" },
  ];
  const lines: string[] = [];
  for (const group of groups) {
    const entries = FACTSET_ACTIVE_GRAPH_CAPABILITIES.filter(
      (entry) => entry.category === group.category && (!allow || allow.has(entry.id)),
    );
    if (entries.length === 0) continue;
    lines.push(`${group.heading}:`);
    for (const entry of entries) {
      lines.push(
        `- \`${entry.id}\` — ${entry.description} (${cardinalityPromptLabel(entry.cardinality)})`,
      );
    }
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}

export function buildFactSetMultiChartIntentPromptBlock(): string {
  return [
    "- If the user asks for a broad chart family, consider a small coherent set of related ActiveGraphs.",
    "- If the user names a specific metric or chart type, create only that requested chart unless they ask for a broader view.",
    "- Use one chart creation call per ActiveGraph and keep identifiers, dates, frequency, and labels consistent.",
    "- Place related charts in a compact non-overlapping grid; default to at most four charts on one slide.",
  ].join("\n");
}
